/**
 * AiPipelineReport — the CEO pipeline AI report, as a DrilldownModal frame.
 *
 * Card P182 PR2 (P180, Carlos 30 Oct). Ask a question about the pipeline in
 * words; get a written brief back, built only from the dashboard's own figures.
 *
 * ⛔⛔ EVERY NUMBER MUST TRACE BACK TO A QUERY. The server returns the payload
 * the model was given alongside the prose, and this panel SHOWS it ("Figures
 * the AI was given"). That is the only way a reader can check the brief rather
 * than trust it — and it is why the report cannot quietly contradict the tiles.
 *
 * ⚠ The question box stays editable after dictation lands in it (P182 PR4), so
 * a misheard word is corrected before it is sent, never after it is answered.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import useDictation, { MAX_CLIP_SECONDS } from '../../lib/useDictation';
import CeoReportView from './CeoReportView';
import { reportToText } from './ceoReportText';
import { formatRangeLabel } from './_reportFormat';
import { WorkingPanel, Spinner, prefersReducedMotion } from './_reportProgress';
import { startJob, subscribeJob, getJob } from '../../lib/ceoReportJob';
import { pushToast, dismissToast } from '../../lib/toast';
import { buildReportModel, buildPdf, collectCharts, pdfFileName } from './reportPdf';
import { payloadToCsv, csvFileName } from './reportCsv';
import { downloadBytes } from '../../lib/download';
import { prepareForPrint, installPrintHooks } from './_printPrep';
import { messageForError } from './_reportErrors';

/*
 * ⚠ ONE KEY FOR THE WHOLE LIFECYCLE. "being generated" must become "ready" IN
 * PLACE; three stacked toasts for one report is the failure mode this avoids.
 */
const TOAST_KEY = 'ceo-ai-report';

const S = {
  wrap:   { display: 'flex', flexDirection: 'column', gap: 12 },
  label:  { fontSize: 12, fontWeight: 700, color: '#374151' },
  box:    { width: '100%', minHeight: 64, padding: 8, border: '1px solid #d1d5db',
            borderRadius: 6, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' },
  row:    { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  btn:    { padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6,
            background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  primary:{ padding: '6px 14px', border: '1px solid #1d4ed8', borderRadius: 6,
            background: '#1d4ed8', color: '#fff', cursor: 'pointer',
            fontSize: 13, fontWeight: 700 },
  note:   { fontSize: 11, color: '#6b7280' },
  err:    { padding: 10, border: '1px solid #fecaca', background: '#fef2f2',
            color: '#991b1b', borderRadius: 6, fontSize: 13, lineHeight: 1.55 },
  /* ⚠ Amber, not red: a truncated report is degraded, not broken, and the
     reader can still use every figure above the cut. */
  warn:   { padding: 10, border: '1px solid #fde68a', background: '#fffbeb',
            color: '#92400E', borderRadius: 6, fontSize: 13, lineHeight: 1.55,
            marginBottom: 10 },
  linkBtn:{ background: 'none', border: 'none', padding: 0, font: 'inherit',
            color: '#1d4ed8', textDecoration: 'underline', cursor: 'pointer' },
  prose:  { fontSize: 13, lineHeight: 1.55, color: '#1f2937' },
  pre:    { maxHeight: 320, overflow: 'auto', background: '#f9fafb',
            border: '1px solid #e5e7eb', borderRadius: 6, padding: 8,
            fontSize: 11, lineHeight: 1.4 },
};

export default function AiPipelineReport({
  period = 'Month',
  /*
   * ⛔ Card P224 item 4 — THE DASHBOARD'S OWN EXPORT GRANT, passed in rather
   * than re-derived. CeoDashboardPage already computes it from
   * analytics:drilldown:export for every other export on that screen; a
   * second derivation here would be a second answer to "may this person take
   * data out". Defaults to FALSE so a caller that forgets it shows no
   * download button, rather than one the server will refuse.
   */
  canExport = false,
  /*
   * ⭐ Card P214 — THE DASHBOARD'S OWN RANGE OBJECT. CeoDashboardPage has
   * passed this since P185 and this component never read it, so the dialog
   * title showed the preset NAME ("Month") while the brief's header showed
   * the server's label ("Custom range"). Two labels for one window, neither
   * naming a day. The same formatter now renders both.
   */
  range = null,
  filters = null,
  onClose,
  /** Injected in tests; defaults to the real bridge. */
  api = (typeof window !== 'undefined' ? window.zignagenixAPI : null),
  /** P182 PR4 wires dictation here. Absent today — the button is not rendered. */
  micSlot = null,
}) {
  const [question, setQuestion]   = useState('');
  // P182 PR3 — '' means all pipelines; 'unassigned' is its own scope.
  const [pipelineId, setPipelineId] = useState('');
  const [pipelines, setPipelines]   = useState(null);
  const [unassigned, setUnassigned] = useState(null);
  const [loading, setLoading]     = useState(false);
  /*
   * ── Card P219 ───────────────────────────────────────────────────────────
   * The clock the working panel counts from. State, not a ref: the panel has
   * to re-render as it ticks.
   */
  const [startedAt, setStartedAt]   = useState(null);
  const [elapsedMs, setElapsedMs]   = useState(0);
  /*
   * ⛔⛔ Card P219 item 8 — THE JOB IS NOT THIS COMPONENT'S. It lives in
   * src/lib/ceoReportJob.js so that closing the dialog does not cancel it and
   * reopening shows the finished report, or the working panel, rather than a
   * blank box and a fifty-second wait paid twice.
   */
  /*
   * ⛔⛔ A REQUEST ID AND A BUSY FLAG. The button's `disabled` attribute stops
   * a second MOUSE click; it does not stop a keyboard repeat, a double-fire
   * from a touch device, or a caller invoking the handler directly — and the
   * card asks for ONE request per click, which an attribute cannot promise.
   * The id additionally makes a superseded answer harmless: without it,
   * whichever call resolves LAST wins, and that can be the older one.
   */
  const busy = useRef(false);
  /*
   * ⚠⚠ REFS, BECAUSE THE SUBSCRIPTION IS MOUNTED ONCE. Its closure captures
   * the FIRST render's handlers, so a toast action wired directly to
   * `generate` would call a stale closure holding the question and pipeline
   * as they were when the dialog first opened. These are repointed every
   * render, so the toast always runs the current one.
   */
  const generateRef = useRef(() => {});
  /* The subscription is mounted once; these keep it pointed at the current
     handlers (same reason as generateRef — P219). */
  const failureRef = useRef(() => {});
  const viewRef = useRef(null);
  const printRef = useRef(null);
  const downloadRef = useRef(null);
  const csvRef = useRef(null);
  const canExportRef = useRef(false);
  const reduced = prefersReducedMotion();
  const [report, setReport]       = useState(null);
  /*
   * ── Card P201 ───────────────────────────────────────────────────────────
   * `structured` is the validated JSON report the server parsed. NULL IS A
   * NORMAL OUTCOME, not an error: the model returned something unparseable,
   * or nothing survived validation, and the raw prose in `report` renders
   * instead — exactly the report that shipped before this card. A visual
   * upgrade must not be able to cost the CEO their brief.
   */
  const [structured, setStructured] = useState(null);
  const [dropped, setDropped]       = useState([]);
  /*
   * ── Card P209 ───────────────────────────────────────────────────────────
   * `truncated` — the model's answer was cut off and what parsed is a
   * PREFIX of the brief. Rendered, with a notice: a partial brief with its
   * figures intact is useful, a partial brief pretending to be whole is not.
   *
   * `sourceWithheld` — the reply was structured output that failed to parse.
   * ⛔⛔ THE SOURCE IS NEVER PUT ON SCREEN. Measured live on v2.1.49: this
   * panel rendered 73 paragraphs of raw JSON at Carlos, beginning "```json"
   * and ending mid-sentence, because the prose fallback could not tell "the
   * model wrote a brief" from "the model wrote broken JSON". The raw text
   * goes to the server log; the reader gets a sentence and a button.
   */
  const [truncated, setTruncated]   = useState(false);
  const [sourceWithheld, setSourceWithheld] = useState(false);
  const [payload, setPayload]     = useState(null);
  const [error, setError]         = useState(null);
  const [showFigures, setShowFigures] = useState(false);
  const [downloading, setDownloading] = useState(null);
  /* Which action a "Try again" would repeat, or null when retrying is futile
     (a missing permission is not fixed by pressing the button again). */
  const [errorRetry, setErrorRetry] = useState(null);
  const retryRef = useRef(null);
  /* The rendered report, so the PDF can rasterise the charts the reader sees. */
  const reportRootRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!api || typeof api.getCeoPipelines !== 'function') { setPipelines([]); return undefined; }
    api.getCeoPipelines().then((r) => {
      if (cancelled) return;
      /*
       * ⚠ A FAILED LOOKUP LEAVES THE PICKER AT "All pipelines" AND SAYS SO
       * BELOW — it does not silently render an empty list that looks like
       * "this clinic has no pipelines". An empty inventory and an unreachable
       * one are different states.
       */
      if (!r || r.error) { setPipelines([]); return; }
      setPipelines(Array.isArray(r.pipelines) ? r.pipelines : []);
      setUnassigned(r.unassigned || null);
    }).catch(() => { if (!cancelled) setPipelines([]); });
    return () => { cancelled = true; };
  }, [api]);

  // ── Dictation (P182 PR4; moved onto the shared hook in P193) ───────────
  // ⛔ The recorder, the 60s cap, the base64 encode and the mic release used to
  // live inline here. P193 needed the same thing for CRM intake and clinical
  // notes, so it moved to src/lib/useDictation.js and this file now has no
  // dictation logic of its own — one implementation, three callers.
  const mic = useDictation({
    send: api && api.dictateCeoQuestion,
    onTranscript: (t) => {
      if (!t) return;
      // ⭐ Lands in the box, editable. Never submitted.
      setQuestion((q) => (q ? `${q.trim()} ${t}`.trim() : t));
    },
  });

  /*
   * ⚠ The header's pipeline label comes from the PICKER, not from the model.
   * It is the one piece of report chrome that must never be a model claim:
   * a brief captioned with the wrong pipeline is wrong in the most damaging
   * way available, because every figure under it then reads as that
   * pipeline's.
   */
  /*
   * ⚠ DATES WHEREVER THERE ARE DATES, the preset name only when there are
   * none. `period` stays the fallback so a caller that passes no range (the
   * tests, and any future embed) still gets a sensible title.
   */
  const rangeTitle = formatRangeLabel(range) || period;

  const pipelineLabel = (() => {
    if (!pipelineId) return 'All pipelines';
    if (pipelineId === 'unassigned') return 'Unassigned — no pipeline';
    const hit = (pipelines || []).find((p) => String(p.pipeline_id) === String(pipelineId));
    return hit ? hit.pipeline : `Pipeline ${pipelineId}`;
  })();

  /**
   * Apply a server answer to the panel's state.
   *
   * ⚠ ONE PLACE, because two now feed it: a job finishing while the dialog is
   * open, and the subscription re-attaching to an ALREADY-FINISHED job when
   * the dialog is reopened. If those diverged, a reopened dialog would render
   * a different report from the one that was generated.
   */
  const applyResult = useCallback((r) => {
    if (!r || r.error) {
      /*
       * ⚠ SHOW THE SERVER'S OWN MESSAGE. An ACCESS_DENIED here means the
       * per-user grant is missing, and "something went wrong" would send
       * someone hunting a bug instead of asking for the grant.
       */
      setError((r && r.message) || 'The report could not be generated.');
      setReport(null); setPayload(null); setStructured(null); setDropped([]);
      setTruncated(false); setSourceWithheld(false);
      return;
    }
    setError(null);
    setReport(r.response || '');
    setPayload(r.data || null);
    setStructured(r.report || null);
    setDropped(Array.isArray(r.droppedCharts) ? r.droppedCharts : []);
    setTruncated(Boolean(r.truncated));
    /*
     * ⚠ The server decides this, not the client: it has the raw reply and the
     * parse outcome together. A reply that could not be structured is
     * withheld whatever it looks like — there is no path left on this surface
     * that renders unparsed model output.
     */
    setSourceWithheld(!r.report);
  }, []);

  /*
   * ⭐ RE-ATTACH ON MOUNT. A dialog opened while a job is running picks up the
   * working panel mid-flight; one opened after it finished shows the report.
   * Neither path re-requests anything.
   */
  useEffect(() => subscribeJob((job) => {
    busy.current = job.status === 'running';
    setLoading(job.status === 'running');
    setStartedAt(job.status === 'running' ? job.startedAt : null);

    if (job.status === 'done' && job.result) {
      applyResult(job.result);
      const failed = job.result.error || !job.result.report;
      /*
       * ⚠ "Ready" ONLY WHEN THERE IS SOMETHING TO READ. A report the server
       * could not structure is a failure to the reader even though the call
       * succeeded, and telling them it is ready would send them to an error
       * panel.
       */
      pushToast(failed ? {
        key: TOAST_KEY, tone: 'error',
        title: "The report couldn't be generated.",
        body: 'Try again — your figures are unaffected.',
        actions: [{ label: 'Regenerate', testId: 'toast-regenerate', onClick: () => generateRef.current && generateRef.current() }],
      } : {
        key: TOAST_KEY, tone: 'success',
        title: 'Your report is ready',
        /* ⚠ Download is offered only when the grant allows it — the toast
           must not advertise a button the dialog does not have. */
        actions: [
          { label: 'View', testId: 'toast-view', onClick: () => viewRef.current && viewRef.current() },
          ...(canExportRef.current ? [{ label: 'Download PDF', testId: 'toast-download',
              onClick: () => downloadRef.current && downloadRef.current() }] : []),
          { label: 'Print', testId: 'toast-print', onClick: () => printRef.current && printRef.current() },
        ],
      });
    } else if (job.status === 'error') {
      setReport(null); setPayload(null); setStructured(null); setDropped([]);
      setTruncated(false); setSourceWithheld(false);
      /* ⛔ Card P230 — the SERVER'S message is logged, not shown. It can carry
         a status code or a Bedrock error; the reader gets the mapped text. */
      failureRef.current(job.error, 'generate');
    } else if (job.status === 'running') {
      /* The previous brief goes NOW, not when the new one lands. */
      setStructured(null); setDropped([]); setReport(null);
      setTruncated(false); setSourceWithheld(false); setError(null);
    }
  /*
   * ⚠⚠ MOUNT-ONCE ON PURPOSE, AND NO eslint-disable COMMENT. The first version
   * carried a disable for react-hooks/exhaustive-deps and FAILED THE
   * PRODUCTION BUILD: that rule is not registered in the build's config, so a
   * disable for an unknown rule is itself an error — and the React Component
   * Tests and Rules of Hooks checks both PASSED, so nothing else caught it.
   * The empty array is CORRECT rather than suppressed: `applyResult` is a
   * useCallback with no deps and is stable for the component's life, and
   * re-running this effect would tear down and re-add the subscription on
   * every render.
   */
  }), []);

  /*
   * ⚠ DRIVEN BY Date.now(), NOT BY COUNTING TICKS. A background tab throttles
   * setInterval to about once a minute, and a counter that incremented per
   * tick would report 0:04 after two minutes away.
   */
  useEffect(() => {
    if (!loading || startedAt == null) return undefined;
    setElapsedMs(Date.now() - startedAt);
    const id = setInterval(() => setElapsedMs(Date.now() - startedAt), 1000);
    return () => clearInterval(id);
  }, [loading, startedAt]);



  const generate = useCallback(() => {
    if (!api || typeof api.generateAIReport !== 'function') {
      setError('The AI bridge is unavailable in this build.');
      return;
    }
    /*
     * ⛔ ONE REQUEST PER CLICK. startJob() refuses while a job is running, so
     * the guarantee lives in the store rather than in a `disabled` attribute
     * — which stops a mouse and nothing else.
     */
    const started = startJob(
      () => api.generateAIReport('ceo', {
        ...(filters || {}),
        question: question.trim() || null,
        pipelineId: pipelineId || null,
      }),
      { pipelineLabel, rangeLabel: rangeTitle },
    );
    if (!started) return;

    /*
     * ⭐ Card P219 item 8 — THE TOAST IS RAISED HERE, NOT BY THE STORE. The
     * store is a plain job runner with no opinion about notification; the
     * surface that knows what the job is for writes the words.
     */
    pushToast({
      key: TOAST_KEY,
      tone: 'working',
      title: 'Your AI pipeline report is being generated.',
      body: 'This takes about a minute — you can keep working.',
    });
  }, [api, filters, question, pipelineId, pipelineLabel, rangeTitle]);

  /*
   * ── Card P201 item 5: print ────────────────────────────────────────────
   *
   * ⛔⛔ SCOPED TO A BODY CLASS, AND THE REASON IS IN App.css ALREADY. A bare
   * `.sidebar { display: none }` under @media print applies to every other
   * print path in this app — the clinical note export, the oversight reports
   * — each of which has its own tuned layout. The education guide solved this
   * the same way and its comment is the record. Adding and removing the class
   * around the call keeps this change to one surface.
   *
   * ⚠ try/finally, not a .then: window.print() is SYNCHRONOUS and blocking in
   * every browser this ships to, but a throw from it (a blocked popup, a
   * printing subsystem error) must not leave the class on the body — the app
   * would then be invisible on the NEXT print from anywhere else.
   */
  /*
   * ⚠ Repointed every render. The subscription above is mounted once and its
   * closure holds the FIRST render's handlers; reading through a ref is what
   * makes a toast raised in minute one still act on minute two's question.
   */
  useEffect(() => {
    generateRef.current = generate;
    failureRef.current = reportFailure;
    /* ⚠ "Try again" repeats the action that FAILED, not always a regenerate:
       a failed PDF should retry the PDF with the report still on screen. */
    retryRef.current = (context) => {
      setError(null); setErrorRetry(null);
      if (context === 'pdf') { downloadRef.current && downloadRef.current(); return; }
      if (context === 'csv') { csvRef.current && csvRef.current(); return; }
      if (context === 'print') { printRef.current && printRef.current(); return; }
      generate();
    };
  });

  /*
   * ── Card P224 — DOWNLOAD ────────────────────────────────────────────────
   *
   * ⚠ The audit call is FIRE-AND-FORGET and deliberately does not gate the
   * download. The file is assembled in the browser; by the time the row is
   * written the bytes already exist, so failing the download because the
   * trail hiccupped would punish the reader for a logging problem. A failed
   * audit is logged server-side (and returns audited:false) rather than
   * pretended away.
   */
  /*
   * ⛔⛔ Card P230 — ONE PLACE TURNS A FAILURE INTO WORDS. Every catch in this
   * file routes through here, so there is no path on which a library name or
   * a status code can reach the screen. The original error goes to the
   * console, where it is for whoever is debugging rather than for whoever is
   * reading the report.
   */
  const reportFailure = useCallback((err, context) => {
    const { cause, text, retry } = messageForError(err, context);
    // eslint-disable-next-line no-console
    console.error(`[AI report] ${context} failed (${cause}):`, err);
    setError(text);
    setErrorRetry(retry ? context : null);
    pushToast({
      key: TOAST_KEY, tone: 'error', title: text,
      actions: retry ? [{ label: 'Try again', testId: 'toast-retry',
                          onClick: () => retryRef.current && retryRef.current(context) }] : [],
    });
  }, []);

  const recordExport = useCallback((format, chartCount) => {
    if (!api || typeof api.auditCeoReportExport !== 'function') return;
    try {
      const r = api.auditCeoReportExport({
        format,
        pipeline: pipelineLabel,
        period: rangeTitle,
        charts: chartCount || 0,
      });
      if (r && typeof r.catch === 'function') r.catch(() => {});
    } catch (_) { /* never costs the download */ }
  }, [api, pipelineLabel, rangeTitle]);

  const downloadPdf = useCallback(async () => {
    if (!structured || downloading) return;
    setDownloading('pdf');
    try {
      const model = buildReportModel({ report: structured, payload, pipelineLabel });
      if (!model) return;
      /*
       * ⭐ Card P225 item 3 — THE SAME PIN FEEDS THE PDF. Without it the
       * document captured each chart at whatever width the reader's window
       * happened to be, so two people exporting the same report got
       * different-sized pictures. Restored before the file is handed over.
       */
      const restore = prepareForPrint(
        reportRootRef.current && reportRootRef.current.closest
          ? reportRootRef.current.closest('.ceo-ai-report') : null);
      let charts;
      try { charts = await collectCharts(reportRootRef.current); }
      finally { restore(); }
      const bytes = await buildPdf(model, charts);
      const name = pdfFileName(pipelineLabel, payload && payload.generated_at);
      /*
       * ⛔⛔ downloadBytes SWALLOWS ITS OWN FAILURE and returns false — a
       * browser that blocks the download throws nothing at all. I handled
       * that for the CSV and missed it here; a red arm planting a failing
       * createObjectURL found it, because the test could not make the PDF
       * path report anything. A download that silently does not happen is
       * the worst of the three outcomes: the reader waits for a file that is
       * never coming and has no reason to try again.
       */
      const ok = downloadBytes(bytes, name, 'application/pdf');
      if (!ok) throw new Error('the browser blocked the download');
      recordExport('pdf', charts.size);
    } catch (err) {
      /* ⛔ THE REPORT STAYS ON SCREEN. A failed export costs a file, never
         the brief the reader is looking at. */
      reportFailure(err, 'pdf');
    } finally {
      setDownloading(null);
    }
  }, [structured, payload, pipelineLabel, downloading, recordExport]);

  const downloadCsv = useCallback(() => {
    if (!payload || downloading) return;
    setDownloading('csv');
    try {
      const csv = payloadToCsv(payload);
      const ok = downloadBytes(csv, csvFileName(pipelineLabel, payload.generated_at),
        'text/csv;charset=utf-8');
      /* ⚠ downloadBytes swallows its own failure and returns false — a
         browser that blocks the download throws nothing. */
      if (!ok) throw new Error('the browser blocked the download');
      recordExport('csv', 0);
    } catch (err) {
      reportFailure(err, 'csv');
    } finally {
      setDownloading(null);
    }
  }, [payload, pipelineLabel, downloading, recordExport]);

  /*
   * ⚠ Card P225 — every OTHER way a print can start. The button below calls
   * prepareForPrint directly (window.print() is synchronous and must not race
   * an event); these cover Cmd-P, the File menu and a reopened preview, which
   * is how Daniel printed when the graphs went missing.
   */
  useEffect(() => installPrintHooks(() => reportRootRef.current
    && reportRootRef.current.closest
    && reportRootRef.current.closest('.ceo-ai-report')), []);

  const printReport = useCallback(() => {
    const cls = 'ceo-ai-report-printing';
    /*
     * ⛔⛔ Card P225 — UN-CLIP BEFORE PRINTING, RESTORE AFTER. The report sits
     * inside DrilldownModal's fixed backdrop, max-height dialog and
     * overflow:auto scroll body; measured with --print-to-pdf, that truncates
     * the printed document to ONE PAGE, taking six of the seven charts with
     * it. The charts themselves were never the problem — they measured
     * 664x240 throughout.
     */
    let restore = () => {};
    try {
      document.body.classList.add(cls);
      restore = prepareForPrint(
        reportRootRef.current && reportRootRef.current.closest
          ? reportRootRef.current.closest('.ceo-ai-report') : null);
      window.print();
    } catch (err) {
      reportFailure(err, 'print');
    } finally {
      restore();
      document.body.classList.remove(cls);
    }
  }, []);

  useEffect(() => {
    downloadRef.current = downloadPdf;
    csvRef.current = downloadCsv;
    canExportRef.current = canExport;
    printRef.current = printReport;
    /* ⭐ "View" brings the dialog back to the top of the page. The report is
       already rendered behind the toast; scrolling to it is the whole job. */
    viewRef.current = () => {
      const el = typeof document !== 'undefined'
        && document.querySelector('[data-testid="ai-pipeline-report"]');
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
  });

  const copy = useCallback((text) => {
    if (!text) return;
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => { /* clipboard denied — no-op */ });
    }
  }, []);

  return (
    <section style={S.wrap} className="ceo-ai-report"
             data-testid="ai-pipeline-report" aria-label="AI pipeline report">
      <div>
        <label style={S.label} htmlFor="ai-pipeline-question">
          Ask about the pipeline — {rangeTitle}
        </label>
        <textarea
          id="ai-pipeline-question"
          data-testid="ai-report-question"
          style={S.box}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g. Which stage is holding the most value, and who owns it?"
        />
        <div style={S.note}>
          Leave this empty for a general brief. The report uses the same figures as
          the tiles on this dashboard, for the period selected there.
          {pipelineId ? (
            <> {' '}⚠ The headline tiles always cover <strong>all</strong> pipelines;
            the per-pipeline figures come from the breakdowns.</>
          ) : null}
          {pipelines && pipelines.length === 0 ? (
            <> {' '}⚠ The pipeline list could not be loaded, so only the
            all-pipelines report is available.</>
          ) : null}
        </div>
      </div>

      {/* ⚠ `ai-report-toolbar` is what the print stylesheet hides: these are
          how you ASK for a brief, not part of the document. The class must
          EXIST or that rule styles nothing — a print block scoped to a class
          nobody renders still parses and still passes a postcss test. */}
      <div style={S.row} className="ai-report-toolbar">
        <label style={S.label} htmlFor="ai-pipeline-scope">Pipeline</label>
        <select
          id="ai-pipeline-scope"
          data-testid="ai-report-pipeline"
          value={pipelineId}
          onChange={(e) => setPipelineId(e.target.value)}
          style={{ ...S.btn, fontWeight: 400 }}
        >
          <option value="">All pipelines</option>
          {(pipelines || []).map((p) => (
            <option key={p.pipeline_id} value={String(p.pipeline_id)}>
              {p.pipeline} ({p.open_opps})
            </option>
          ))}
          {unassigned && Number(unassigned.open_opps) > 0 ? (
            /* ⛔ Selectable ON PURPOSE. These opportunities belong to no
               pipeline and appear in no per-pipeline report; a picker that
               hid them would make the per-pipeline figures look complete. */
            <option value="unassigned">
              Unassigned — no pipeline ({unassigned.open_opps})
            </option>
          ) : null}
        </select>
        <button type="button" style={{ ...S.primary, display: 'inline-flex',
                                       alignItems: 'center', gap: 7 }}
                data-testid="ai-report-generate"
                disabled={loading} aria-busy={loading ? 'true' : undefined}
                onClick={generate}>
          {loading ? <Spinner reduced={reduced} size={12} /> : null}
          {loading ? 'Generating…' : 'Generate report'}
          {/*
            * ⛔ Card P230 — a screen reader gets the state in words. aria-busy
            * marks the control as working but is not announced as a label;
            * the spinner is aria-hidden by design, so without this a
            * non-sighted reader hears only "Generating…, dimmed" and has no
            * confirmation that a report is actually being produced.
            */}
          {loading ? (
            <span data-testid="ai-report-busy-sr" style={{
              position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
              overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
            }}>
              Generating report
            </span>
          ) : null}
        </button>
        {mic.available ? (
          <>
            <button
              type="button"
              style={mic.state === 'recording' ? { ...S.btn, borderColor: '#b91c1c', color: '#b91c1c' } : S.btn}
              data-testid="ai-report-mic"
              disabled={mic.state === 'transcribing'}
              aria-pressed={mic.state === 'recording'}
              onClick={mic.state === 'recording' ? mic.stop : mic.start}
            >
              {/* ⚠ Card P219 item 7 — the same silence, one button over:
                  "Transcribing…" with nothing moving reads as stuck. */}
              {mic.state === 'transcribing'
                ? <><Spinner reduced={reduced} size={12} /> Transcribing…</>
                : mic.state === 'recording' ? `■ Stop (${mic.elapsed}s)` : '🎤 Dictate'}
            </button>
            {mic.state === 'recording' ? (
              <span style={S.note} data-testid="ai-report-mic-cap">
                Max {MAX_CLIP_SECONDS}s — stops on its own
              </span>
            ) : null}
          </>
        ) : null}
        {micSlot}
        {/*
          * ⛔⛔ Card P209 item (d) — COPY AND PRINT FOLLOW THE RENDERED VIEW,
          * so they are offered only when there IS one. The old condition was
          * `report` (the raw reply), which meant that on v2.1.49 the Copy
          * button was live over unparsed JSON and would have put ```json and
          * a severed sentence on the clipboard, and Print would have sent it
          * to a printer. An export of something the screen refuses to show
          * is the same leak through a different door.
          */}
        {structured ? (
          <>
            <button type="button" style={S.btn} data-testid="ai-report-copy"
                    onClick={() => copy(reportToText(structured, payload, { pipelineLabel }))}>
              Copy report
            </button>
            <button type="button" style={S.btn} data-testid="ai-report-print"
                    onClick={printReport}>Print</button>
            {/*
              * ⛔⛔ Card P224 item 4 — DOWNLOAD IS ABSENT WITHOUT THE GRANT,
              * not disabled. A disabled control advertises a capability and
              * then refuses it, which reads as a fault; a reader without
              * analytics:drilldown:export simply has no download here, the
              * same way they have no CSV on the drill-downs.
              *
              * ⚠ It sits INSIDE the `structured` branch, so it cannot exist
              * before a report does — the card's "disabled before a report
              * exists" is satisfied by the button not being rendered at all,
              * which is the stronger version.
              */}
            {canExport ? (
              <>
                <button type="button" style={S.btn} data-testid="ai-report-download-pdf"
                        disabled={Boolean(downloading)}
                        aria-busy={downloading === 'pdf' ? 'true' : undefined}
                        onClick={downloadPdf}>
                  {downloading === 'pdf' ? 'Building PDF…' : 'Download PDF'}
                </button>
                <button type="button" style={S.btn} data-testid="ai-report-download-csv"
                        disabled={Boolean(downloading)}
                        onClick={downloadCsv}>
                  Download figures
                </button>
              </>
            ) : null}
          </>
        ) : null}
        {/*
          * ⚠ THE FIGURES BUTTON IS NOT INSIDE THAT BRANCH, DELIBERATELY. It
          * is built from the payload, never from the model's reply, so it
          * works exactly when the report does not — which is the moment the
          * reader most needs the numbers. Gating it on a successful parse
          * would remove the one thing still standing.
          */}
        {payload ? (
          <button type="button" style={S.btn} data-testid="ai-report-figures"
                  aria-expanded={showFigures}
                  onClick={() => setShowFigures((v) => !v)}>
            {showFigures ? 'Hide figures' : 'Figures the AI was given'}
          </button>
        ) : null}
        {typeof onClose === 'function' ? (
          <button type="button" style={S.btn} data-testid="ai-report-close"
                  onClick={onClose}>Close</button>
        ) : null}
      </div>

      {mic.error ? (
        <div style={S.err} data-testid="ai-report-mic-error" role="alert">{mic.error}</div>
      ) : null}
      {error ? (
        <div style={S.err} data-testid="ai-report-error" role="alert">
          {error}
          {/* ⛔ Card P230 — "Try again" only where retrying can help. A missing
              permission is not fixed by pressing the button again, and
              offering it teaches the reader to keep pressing. */}
          {errorRetry ? (
            <>
              {' '}
              <button type="button" style={S.linkBtn} data-testid="ai-report-try-again"
                      disabled={loading || Boolean(downloading)}
                      onClick={() => retryRef.current && retryRef.current(errorRetry)}>
                Try again
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        /*
         * ⛔ FIRST IN THE CHAIN, DELIBERATELY. Anything above this that could
         * render a previous report would put a stale brief under a spinner —
         * which is the defect, not the fix.
         */
        <WorkingPanel elapsedMs={elapsedMs} reduced={reduced}
                      pipelineLabel={pipelineLabel} rangeLabel={rangeTitle} />
      ) : structured ? (
        <>
          {truncated ? (
            /*
             * ⚠ A PREFIX, SAID OUT LOUD. What follows parsed cleanly but the
             * model's answer was severed, so sections and actions after the
             * cut are simply missing — not zero, not "nothing to report".
             * Leaving that unsaid is how a reader acts on half a brief.
             */
            <div style={S.warn} data-testid="ai-report-truncated" role="status">
              ⚠ This report was cut short — the AI's answer hit its length limit, so
              anything after the last section below is missing.{' '}
              <button type="button" style={S.linkBtn} data-testid="ai-report-regenerate-truncated"
                      onClick={generate} disabled={loading}>
                {loading ? 'Regenerating…' : 'Regenerate'}
              </button>
            </div>
          ) : null}
          <CeoReportView
            rootRef={reportRootRef}
            report={structured}
            payload={payload}
            droppedCharts={dropped}
            pipelineLabel={pipelineLabel}
          />
        </>
      ) : sourceWithheld ? (
        /*
         * ⛔⛔ THE SOURCE IS NOT SHOWN. This is the v2.1.49 failure's new
         * face: one sentence and a button, instead of 73 paragraphs of raw
         * JSON ending mid-word. The reader still gets the numbers — "Figures
         * the AI was given" is built from the payload and never depended on
         * the model's reply at all, so it works precisely when the prose
         * does not.
         */
        <div style={S.err} data-testid="ai-report-unreadable" role="alert">
          <strong>The AI's answer couldn't be read.</strong> Nothing is wrong with
          your figures — open “Figures the AI was given” below to see them, and
          try again.{' '}
          <button type="button" style={S.linkBtn} data-testid="ai-report-regenerate"
                  onClick={generate} disabled={loading}>
            {loading ? 'Regenerating…' : 'Regenerate'}
          </button>
        </div>
      ) : null}

      {/*
        * ⛔ PRINT-ONLY, and hidden on screen by its own inline style rather
        * than by a class the stylesheet has to remember. It carries the same
        * identity the header does, so a loose sheet from a board pack can
        * still be traced to a pipeline and a window.
        */}
      {structured ? (
        <div className="ceo-print-footer" data-testid="ai-report-print-footer"
             style={{ display: 'none' }}>
          ZignaGenix · AI pipeline report · {pipelineLabel}
          {rangeTitle ? ` · ${rangeTitle}` : ''}
        </div>
      ) : null}

      {showFigures && payload ? (
        <div>
          <div style={S.row}>
            <span style={S.label}>Figures the AI was given</span>
            <button type="button" style={S.btn} data-testid="ai-report-copy-figures"
                    onClick={() => copy(JSON.stringify(payload, null, 2))}>Copy figures</button>
          </div>
          <div style={S.note}>
            ⚠ Every number in the report above must appear here. If one does not,
            the report is wrong and should be reported as a defect.
          </div>
          <pre style={S.pre} data-testid="ai-report-payload">
            {JSON.stringify(payload, null, 2)}
          </pre>
        </div>
      ) : null}
    </section>
  );
}
